import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { registerUnauthorizedHandler } from "../api/client";
import {
  clearJwt,
  readJwt,
  readLastEmail,
  writeJwt,
  writeLastEmail,
} from "./storage";

/**
 * Auth state container. Wraps the JWT-in-localStorage convention from
 * frontend/CLAUDE.md and registers a router-aware 401 handler with the
 * API client so any expired token bounces the user back to /login.
 *
 * The token's payload is *not* decoded here — the backend treats the
 * JWT as opaque and so do we. Logout works by clearing local storage
 * regardless of token validity.
 */

export interface AuthState {
  jwt: string | null;
  isAuthenticated: boolean;
  lastEmail: string | null;
  login: (token: string, email: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [jwt, setJwt] = useState<string | null>(() => readJwt());
  const [lastEmail, setLastEmail] = useState<string | null>(() => readLastEmail());

  const logout = useCallback(() => {
    clearJwt();
    setJwt(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const login = useCallback((token: string, email: string) => {
    writeJwt(token);
    writeLastEmail(email);
    setJwt(token);
    setLastEmail(email);
  }, []);

  // Tie 401 handling to the router so the API client doesn't need
  // to know how navigation works.
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setJwt(null);
      navigate("/login", { replace: true });
    });
    return () => registerUnauthorizedHandler(null);
  }, [navigate]);

  const value = useMemo<AuthState>(
    () => ({
      jwt,
      isAuthenticated: jwt !== null,
      lastEmail,
      login,
      logout,
    }),
    [jwt, lastEmail, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
